import { useCallback, useState } from "react";

interface UseDropdownCloseControllerInput {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  animateClose: boolean;
  optionCount: number;
  isActiveIndexControlled: boolean;
  setInternalActiveIndex: (index: number | null) => void;
  setHoveredIndex: (index: number | null) => void;
}

interface UseDropdownCloseControllerResult {
  isAnimating: boolean;
  isClosing: boolean;
  setIsAnimating: (isAnimating: boolean) => void;
  handleClose: () => void;
}

export function useDropdownCloseController({
  isOpen,
  setIsOpen,
  animateClose,
  optionCount,
  isActiveIndexControlled,
  setInternalActiveIndex,
  setHoveredIndex,
}: UseDropdownCloseControllerInput): UseDropdownCloseControllerResult {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const clearTransientState = useCallback(() => {
    setHoveredIndex(null);
    if (!isActiveIndexControlled) {
      setInternalActiveIndex(null);
    }
  }, [isActiveIndexControlled, setHoveredIndex, setInternalActiveIndex]);

  const handleClose = useCallback(() => {
    if (!isOpen) {
      return;
    }

    if (!animateClose) {
      setIsOpen(false);
      setIsClosing(false);
      clearTransientState();
      return;
    }

    setIsClosing(true);
    setIsAnimating(false);

    const animationDuration = optionCount * 50 + 200;
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
      clearTransientState();
    }, animationDuration);
  }, [animateClose, clearTransientState, isOpen, optionCount, setIsOpen]);

  return {
    isAnimating,
    isClosing,
    setIsAnimating,
    handleClose,
  };
}
